import { EFFECT_MANIFEST } from './manifest.js';
import { TEMPO_EPSILON } from '../../../../sync/pulseClock.js';
import { clampTempo } from '../../../../sync/tempoRange.js';
import { isDebugSyncLoggingEnabled } from '../../../../sync/debugSync.js';

const RATE_EPSILON = 0.0005;

function toNumber(value, fallback = null) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function clamp(value, min, max) {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function semitoneToRatio(semitones, Tone) {
  const numeric = toNumber(semitones, 0);
  if (!Number.isFinite(numeric)) return 1;
  if (Tone?.intervalToFrequencyRatio) {
    return Tone.intervalToFrequencyRatio(numeric);
  }
  return Math.pow(2, numeric / 12);
}

function mapPercentValue(value, range = {}) {
  const min = toNumber(range?.min, -100);
  const max = toNumber(range?.max, 100);
  const percent = clamp(toNumber(value, 0), min, max);
  const rateFactor = 1 + percent / 100;
  const playbackRate = Math.max(0.01, rateFactor);
  return { playbackRate };
}

function mapSemitoneValue(value, range, Tone, quantizeStep = 1) {
  const min = toNumber(range?.min, -12);
  const max = toNumber(range?.max, 12);
  const clamped = clamp(toNumber(value, 0), min, max);
  const snapped = Math.round(clamped / quantizeStep) * quantizeStep;
  const playbackRate = semitoneToRatio(snapped, Tone);
  return { playbackRate, semitones: snapped };
}

function createTempoPitchModule({ moduleSpec, playbackController, Tone, sharedState, deck }) {
  const range = moduleSpec.valueRange || EFFECT_MANIFEST.userParamSpec?.range || null;
  const quantize = range?.quantize;
  const isQuantized = quantize?.unit === 'st';
  const quantizeStep = Number(quantize?.step ?? 1) || 1;
  // A TEMPO module (tempoFine/tempoWide/tempoStretch*) is the deck's PITCH FADER — it controls tempo and,
  // when this deck is following, DRIVES the shared master. A PITCH module (pitchFine/pitchWide/pitchPure*)
  // shifts pitch only and never touches the master tempo.
  const isTempoModule = typeof moduleSpec?.id === 'string' && moduleSpec.id.startsWith('tempo');
  // Stretch-engine axes: a 'stretch' rate module keeps pitch locked while tempo moves; an 'engine'
  // pitch module shifts semitones while tempo stays. Both degrade to the classic tape behavior when
  // the active playback sink has no stretch engine (the setters below report unhandled).
  const wantsStretchRate = moduleSpec?.rateMode === 'stretch';
  const wantsEnginePitch = moduleSpec?.pitchMode === 'engine';
  // A speed-and-reverse module maps ONE bipolar input to a signed velocity:
  // 0 = stop, +full = 1x forward, -full = 1x reverse, linear in between. The
  // magnitude drives the rate and the sign drives the read DIRECTION, combined
  // ONLY here — so there is still one rate owner. The sink glides to silence at
  // ~0 speed (a true stop) and reads its one buffer backwards on reverse.
  const isSpeedReverse = moduleSpec?.speedMode === 'speedReverse';
  // Every module that OWNS its deck's transport tempo while following. Speed-and-reverse joined the
  // tempo faders here: it is a speed control, and a speed control on a warped deck that does not
  // move the tempo leaves the deck playing off the grid the moment it is touched (its centre used
  // to play the track's NATIVE tempo while every sibling played the room's). Only the MAGNITUDE
  // reaches the tempo — there is no negative tempo — and the sign stays a read direction.
  const isTempoDriver = isTempoModule || isSpeedReverse;
  let lastReverse = false;

  const commitReverse = (reversed) => {
    if (lastReverse === reversed) return;
    lastReverse = reversed;
    // Async at the sink (reschedules the engine at a signed rate); direction is
    // the only effect, so a failed switch just leaves the current direction.
    void Promise.resolve(playbackController?.setPlaybackReverse?.(reversed))
      .catch((error) => {
        console.warn('[ToneTempoPitchEffect] Failed to switch playback direction.', error);
      });
  };

  const commitPitchSemitones = (semitones) => {
    if (!playbackController || typeof playbackController.setPitchSemitones !== 'function') return false;
    return playbackController.setPitchSemitones(semitones) === true;
  };

  const pushRateMode = () => {
    playbackController?.setRateMode?.(wantsStretchRate ? 'stretch' : 'varispeed');
  };

  let automationBridge = null;
  let lastPlaybackRate = null;
  // The automation TARGET the current rate was last written to. Identity, not value: a rebind pass
  // hands over a brand-new bridge object onto the SAME param (which already holds the rate — do not
  // re-assert), while a backend swap produces a genuinely NEW param (which has never seen it and
  // must be seeded, or the deck silently drops to 1x).
  let lastWrittenTarget = null;
  let lastUserPlaybackRate = null;
  let lastInput = null;
  let isActive = false;
  // DJ-deck anchor (this module's own): the transport tempo this fader multiplies from, so the
  // drive is T = anchorM × f — one multiplication, and f is the fader's own factor (1 + knob% on a
  // tempo fader, |speed| on speed-and-reverse). The fader therefore means exactly what it reads,
  // and returning it to a position always returns the same tempo.
  //
  // It used to carry a second half, `anchorUserRate`, and drive T = anchorM × (1 + (f − anchorU)) —
  // a DEVIATION from wherever the fader happened to be when the anchor was captured. Because the
  // anchor re-captured at any parked position, one external tempo write while the fader sat away
  // from equilibrium permanently rescaled it: parked low, centre came back at ~2× the transport;
  // parked high, the tempo stuck down and the fader could no longer lift it. Multiplying from a
  // pure anchor has no such state to corrupt.
  //
  // null until the deck is following with a known master.
  let anchorM = null;

  const commitPlaybackRate = (playbackRate) => {
    if (!playbackController || !Number.isFinite(playbackRate)) return;
    if (
      lastPlaybackRate !== null &&
      Math.abs(lastPlaybackRate - playbackRate) <= RATE_EPSILON
    ) {
      return;
    }
    if (isDebugSyncLoggingEnabled()) {
      console.debug('[tempoPitch] commitPlaybackRate', { from: lastPlaybackRate, to: playbackRate });
    }
    if (automationBridge?.ramp) {
      automationBridge.ramp(playbackRate);
    } else {
      playbackController?.setPlaybackRate?.(playbackRate);
    }
    lastPlaybackRate = playbackRate;
    lastWrittenTarget = automationBridge?.ramp ? (automationBridge.target ?? automationBridge) : null;
  };

  // Whether this deck time-stretches to its transport tempo — the DECK owns the rule (warp on;
  // synced OR an unsynced collection deck on its own transport; solo additionally requires the sync
  // session enabled). Warp off lets any deck run free at its own natural tempo.
  const isFollowing = () => sharedState.following === true;

  /** This fader's own factor: 1 + knob% on a tempo fader, |speed| on speed-and-reverse. 1 until a
   *  value has been applied, which is the fader's equilibrium in both mappings. */
  const driveFactor = () => (Number.isFinite(lastUserPlaybackRate) ? lastUserPlaybackRate : 1);

  // Re-capture the anchor whenever the deck's transport tempo moved from a source OTHER than this
  // fader — the number, another deck (via the master), or sync/warp just turning following on.
  // Stateless test: the transport this fader accounts for is anchorM × f, so anything else is
  // someone else's write and becomes the fader's new base. Keeping this classifier matters more
  // than the jump it costs: without it a parked fader under sensor or MIDI automation would keep
  // re-asserting its stale base and fight every external tempo change, value after value.
  //
  // The cost, stated plainly: while the fader sits away from equilibrium and someone else moves the
  // transport, this fader's position no longer describes the tempo, and its NEXT move takes the
  // transport over at that position (anchorM × f). A fader is an absolute control; touching it
  // asserts what it reads.
  const reconcileAnchor = () => {
    if (!isTempoDriver) return;
    if (!isFollowing()) {
      anchorM = null;
      return;
    }
    const master = toNumber(sharedState.transportBpm, null);
    if (!(master > 0)) return;
    // Predict what the deck actually HOLDS, clamp included — not what this fader asked for. A fader
    // driven past either end of the transport's range gets its own write back pinned at that end;
    // comparing against the raw request would read that as someone else's change and re-base the
    // anchor onto the range limit. The fader's whole scale would then be that limit — centre
    // meaning 1 BPM, with only the transport bar able to escape — which is the same "stuck at the
    // floor" the old deviation algebra produced, one level down.
    const expectedM = anchorM > 0 ? clampTempo(anchorM * driveFactor()) : null;
    if (expectedM === null || Math.abs(master - expectedM) > TEMPO_EPSILON) {
      anchorM = master;
      // The transport is no longer this fader's doing, so nothing of its request is outstanding:
      // the deck plays the grid until the fader is moved again.
      driveResidual = 1;
    }
  };

  // What the transport could NOT take of this fader's LAST request, as a multiplier for the deck's
  // own rate. Exactly 1 whenever the request landed inside the transport's range — the
  // overwhelmingly common case, where the tempo carries the whole fader and the deck simply plays
  // its grid. It leaves 1 only at the ends of the range, and at the speed-and-reverse stop (f = 0,
  // where it goes to 0) — which is what makes the stop a real stop rather than the deck crawling
  // along at the minimum tempo.
  //
  // It is STATE, written only where this fader states a value, never derived live from the anchor.
  // Derived live it would describe a drive that never happened: after an external write re-bases
  // the anchor onto the new transport, a fader parked high would compute a residual > 1 and play
  // this deck off the grid without anyone having touched it.
  let driveResidual = 1;

  /** Drive this deck's transport from the fader, and record what the range refused. The deck routes
   *  the write (synced proposes the shared master through the coordinator's one gate; unsynced
   *  moves only this deck's tempo) and clamps it; whatever it could not take stays on this deck. */
  const recordDriveResidual = () => {
    if (!isTempoDriver || !(anchorM > 0)) {
      driveResidual = 1;
      return;
    }
    const wanted = anchorM * driveFactor();
    if (!Number.isFinite(wanted) || wanted <= 0) {
      driveResidual = 0; // f = 0: the fader asked for a standstill
      return;
    }
    const applied = clampTempo(wanted);
    driveResidual = applied > 0 ? wanted / applied : 1;
  };

  const driveTransport = () => {
    if (!isTempoDriver || !(anchorM > 0)) return;
    const proposed = anchorM * driveFactor();
    if (Number.isFinite(proposed) && proposed > 0) {
      deck?.setTempo(proposed, { sourceType: 'module' });
    }
  };

  const applyEffectivePlaybackRate = () => {
    if (!isActive) return;
    const userRate = Number.isFinite(lastUserPlaybackRate) ? lastUserPlaybackRate : 1;
    // DJ-deck table. Following: a tempo deck plays the master (baseRate = M / trackBpm); a pitch deck rides
    // its semitone ratio on top of the master (baseRate × userRate). Independent (not following): the fader
    // is the deck's own control — 0% = the song's native tempo (rate = 1 + knob%) for a tempo deck, or a
    // pitch shift on the native track for a pitch deck.
    const baseRate = toNumber(sharedState.baseRate, 1);
    let rate;
    if (isFollowing() && isTempoDriver) {
      // The fader's factor went into the TRANSPORT, so followRatio (transport / native) already
      // carries it — apart from whatever the transport's range refused, which `driveResidual`
      // hands back here. A tempo fader mid-range leaves that term at exactly 1, i.e. "play the
      // grid", unchanged from before.
      rate = baseRate * driveResidual;
    } else if (isFollowing()) {
      // A PITCH module never touches the tempo: it rides its semitone ratio on top of the grid.
      rate = baseRate * userRate;
    } else {
      // Independent deck (warp off, or no deck feed): the fader is the deck's own control —
      // 0% = the song's native tempo.
      rate = userRate;
    }
    // The 0.01 floor is the sink's, not a tempo decision: the stretch engine remembers the
    // commanded magnitude pre-floor and gates to silence below its own stop epsilon (0.02), so a
    // residual of 0 still lands as a real stop rather than a crawl.
    commitPlaybackRate(Math.max(0.01, Number.isFinite(rate) ? rate : 1));
  };

  // Applications that are RESTORING a value rather than expressing a user gesture. They must never
  // retune the room — loading a world whose fader was saved off equilibrium would otherwise drag
  // every synced deck to that saved position the moment it mounted. All five reach `applyValue`:
  //   slot-create / slot-update  the rack building or re-applying a slot
  //   activate                   this module being selected (below)
  //   rack-sync                  the rack pushing its live input after any graph change, which
  //                              fires on world load right after slot-create with a DIFFERENT value
  //                              (the rack's input, not the saved one), so the duplicate guard
  //                              does not cover it
  //   subscribe                  ParameterManager replaying the current value to a new axis
  //                              subscriber (voice init, a newly registered dimension) — the same
  //                              replay the sync-bpm bridge already ignores for the same reason
  const RESTORE_REASONS = new Set(['slot-create', 'slot-update', 'activate', 'rack-sync', 'subscribe']);
  const isRestore = (context) => RESTORE_REASONS.has(context?.reason);

  const applyValue = (value, context = {}) => {
    if (!playbackController) return;
    if (isSpeedReverse) {
      // Same duplicate guard the tempo path has always had. It matters more now that this module
      // writes a tempo: an unchanged sensor or MIDI stream would otherwise re-propose the transport
      // on every frame. Activation is exempt — it re-applies the held value deliberately.
      if (value === lastInput && lastPlaybackRate !== null && context?.reason !== 'activate') return;
      lastInput = value;
      if (!isActive) return;
      // Linear map from the bipolar input onto the module's SIGNED rate range
      // (signalRange, e.g. −1..+3): the input equilibrium sits at 1× normal
      // play, one extreme reaches reverse and the other a fast forward, with a
      // true stop where the rate crosses 0 (the sink glides to silence there).
      const min = toNumber(range?.min, -100);
      const max = toNumber(range?.max, 100);
      const sr = moduleSpec?.control?.signalRange || {};
      const rateMin = toNumber(sr.min, -1);
      const rateMax = toNumber(sr.max, 3);
      const span = max - min;
      const t = span > 0 ? (clamp(toNumber(value, 0), min, max) - min) / span : 0.5;
      const signed = rateMin + t * (rateMax - rateMin);
      // Sign = read direction, magnitude = the fader's factor; combined only here. Only the
      // magnitude goes on to the transport — there is no negative tempo.
      commitReverse(signed < 0);
      lastUserPlaybackRate = Math.abs(signed);
      // Recorded on every application, restores included, so a module activated with its knob held
      // at the stop is silent rather than playing the grid.
      recordDriveResidual();
      if (!isRestore(context)) driveTransport();
      applyEffectivePlaybackRate();
      return;
    }
    if (value === lastInput && lastPlaybackRate !== null) return;
    lastInput = value;

    const mapping = isQuantized
      ? mapSemitoneValue(value, range, Tone, quantizeStep)
      : mapPercentValue(value, range);

    // Pure-pitch module on the stretch engine: semitones land on the engine
    // and the rate side stays neutral (a following deck still tracks its
    // transport tempo). If the sink has no engine, fall through to the
    // classic tape mapping below.
    if (wantsEnginePitch && commitPitchSemitones(mapping.semitones ?? 0)) {
      lastUserPlaybackRate = 1;
      applyEffectivePlaybackRate();
      return;
    }

    lastUserPlaybackRate = mapping.playbackRate;

    // A following TEMPO fader drives the deck's TRANSPORT tempo: T = anchorM × f. `anchorM` is only
    // ever non-null while following (reconcileAnchor nulls it the instant following drops), so it
    // alone gates whether there is anything to propose.
    recordDriveResidual();
    if (!isRestore(context)) driveTransport();
    applyEffectivePlaybackRate();
  };

  return {
    id: moduleSpec.id,
    label: moduleSpec.label,
    description: moduleSpec.description,
    inputParam: EFFECT_MANIFEST.inputParam,
    toneProperty: moduleSpec.target,
    tonePropertyDescription: moduleSpec.description,
    valueRange: range,
    applyValue,
    // Pure-pitch and speed-and-reverse modules drive the engine through applyValue,
    // never the rate param — exposing it would let automation ramp the RATE and
    // lose the sign (direction) a speed-and-reverse knob carries.
    getTargetParam: () =>
      (wantsEnginePitch || isSpeedReverse) ? null : playbackController?.getPlaybackRateParam?.() ?? null,
    // Re-apply the rate when the deck's transport tempo, sync-enable, or warp flag changes
    // (baseRate/transportBpm are updated before this fires). reconcileAnchor handles a false→true
    // follow transition, so the anchor is valid before the next local drive.
    handleSyncChange() {
      reconcileAnchor();
      applyEffectivePlaybackRate();
    },
    setIsActive(active) {
      isActive = active === true;
      if (isSpeedReverse) {
        // Speed + direction from one input. Activating pushes the flavor (tape
        // vs stretch) and applies the held value; leaving restores forward so a
        // removed module can't strand the track playing backwards.
        if (isActive) {
          pushRateMode();
          // Capture the anchor BEFORE the held value goes out, so this module multiplies from the
          // transport as it stands right now rather than from whatever it last saw. Selecting the
          // module is not a gesture, so the held value is applied as a restore: it sets this deck's
          // own speed without retuning the room.
          reconcileAnchor();
          if (lastInput !== null) applyValue(lastInput, { reason: 'activate' });
          else applyEffectivePlaybackRate();
        } else {
          commitReverse(false);
        }
        return;
      }
      if (isActive) {
        // The active module owns how the sink interprets rate (tape vs stretch).
        pushRateMode();
        reconcileAnchor();
        applyEffectivePlaybackRate();
        if (wantsEnginePitch && lastInput !== null) {
          const mapping = mapSemitoneValue(lastInput, range, Tone, quantizeStep);
          commitPitchSemitones(mapping.semitones ?? 0);
        }
      } else if (wantsEnginePitch) {
        // Leaving a pure-pitch module must not freeze its shift on the voice.
        commitPitchSemitones(0);
      }
    },
    setAutomationBridge(bridge) {
      automationBridge = bridge;
      if (!bridge || lastPlaybackRate === null) return;
      const target = bridge.target ?? bridge;
      // Re-attach onto the sink that already holds this rate: nothing to assert. Re-ramping here on
      // every rebind pass is what fed a redundant write into the stretch engine each time the rack
      // refreshed its bridges. A DIFFERENT target has never seen the rate, so it still gets seeded.
      if (target === lastWrittenTarget) return;
      lastWrittenTarget = target;
      bridge.ramp(lastPlaybackRate);
    },
  };
}

export function createToneTempoPitchEffect({ Tone, settings, deck = null } = {}) {
  if (!Tone?.Gain) {
    throw new Error('[ToneTempoPitchEffect] Tone.Gain constructor is required.');
  }

  const playbackController = settings?.playbackController ?? null;
  const node = new Tone.Gain(1);
  // sharedState (effect-wide), all read from the DECK (the one owner of the follow rule):
  // baseRate = deck.followRatio (transport / native while following, exactly 1 otherwise);
  // transportBpm = the deck's transport tempo (the anchor space the fader drives in — the shared
  // master while synced, the deck's own tempo while unsynced); following = deck.following.
  const sharedState = { baseRate: 1, transportBpm: null, following: false };

  const modules = EFFECT_MANIFEST.modules.map((moduleSpec) =>
    createTempoPitchModule({ moduleSpec, playbackController, Tone, sharedState, deck }),
  );
  let activeModuleId = modules[0]?.id ?? null;

  const applyDeckSnapshot = (snapshot) => {
    const ratio = toNumber(snapshot?.baseRate, null);
    sharedState.baseRate = ratio && ratio > 0 ? ratio : 1;
    const transport = toNumber(snapshot?.bpm, null);
    if (transport && transport > 0) sharedState.transportBpm = transport;
    sharedState.following = snapshot?.following === true;
  };

  const handleDeckChange = (snapshot, reason) => {
    if (reason !== 'bpm' && reason !== 'sync-status') return;
    applyDeckSnapshot(snapshot);
    const activeModule = modules.find((module) => module.id === activeModuleId);
    activeModule?.handleSyncChange?.();
  };

  // Construction-time seed so a voice that mounts mid-session reads current truth (its deck's
  // transport tempo, follow ratio, and follow state) before its first drive — the anchor is then
  // captured by the setIsActive() reconcile below. Live updates arrive via the deck's change stream;
  // deck-less paths keep the independent defaults (not following, ratio 1).
  if (typeof window !== 'undefined' && deck) {
    applyDeckSnapshot(deck.getSnapshot());
  }

  const unsubDeck = deck ? deck.onChange(handleDeckChange) : null;

  modules.forEach((module) => module.setIsActive?.(module.id === activeModuleId));

  return {
    id: EFFECT_MANIFEST.id,
    label: EFFECT_MANIFEST.label,
    version: EFFECT_MANIFEST.version,
    inputParam: EFFECT_MANIFEST.inputParam,
    node,
    modules,
    configureModule(moduleId) {
      activeModuleId = moduleId;
      modules.forEach((module) => module.setIsActive?.(module.id === moduleId));
    },
    dispose() {
      unsubDeck?.();
      // Return the sink to its defaults so a removed effect leaves no frozen
      // tape mode, pitch shift, or reversed direction behind (no-ops on sinks
      // without the engine).
      playbackController?.setRateMode?.('stretch');
      playbackController?.setPitchSemitones?.(0);
      void Promise.resolve(playbackController?.setPlaybackReverse?.(false)).catch(() => {});
      modules.splice(0, modules.length);
      if (typeof node.dispose === 'function') {
        node.dispose();
      }
    },
    manifest: EFFECT_MANIFEST,
  };
}

export default createToneTempoPitchEffect;
